import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError } from 'axios';

import { captureAdminDataAccessError } from './capture-admin-data-access-error';

/**
 * Creates a wrapper for making PATCH requests to an API endpoint with error handling and exception capture.
 *
 * @template Response - The expected response type from the API
 * @param url - The base URL endpoint for the PATCH request (without trailing slash)
 * @param token - Optional authentication token to be used with the API request
 * @param captureException - Optional exception capture mode:
 *   - 'admin': Captures exceptions using the shared admin API capture helper
 *   - 'buyer': Captures exceptions using buyerCaptureException
 *   - undefined: No exception capturing
 * @param mapError - Optional function to transform error messages before throwing
 *   - Receives (error, message) parameters and returns a custom error message
 *
 * @returns Object containing the patchData function for making PATCH requests
 *
 * @example
 * ```typescript
 * const { patchData } = PatchDataWrapper<UserResponse>(
 *   '/api/users',
 *   'your-auth-token',
 *   'admin',
 *   (error, message) => `Custom error: ${message}`
 * );
 *
 * // Update user with ID 123
 * const result = await patchData({ name: 'John' }, 123);
 *
 * // Update with optional path
 * const result = await patchData({ status: 'active' }, undefined, 'profile');
 * ```
 */
export function PatchDataWrapper<Response>(
  url: string,
  token?: string,
  captureException?: 'admin' | 'buyer',
  mapError?: (error?: string, message?: string) => string
) {
  /**
   * Makes a PATCH request to the configured endpoint with the provided payload.
   *
   * @template T - The type of the payload being sent
   * @param payload - The data to be sent in the PATCH request body
   * @param id - Optional ID to append to the URL path (e.g., /api/users/123)
   * @param optionalPath - Optional path segment to use instead of ID (takes precedence over id)
   *
   * @returns Promise resolving to the API response data, or undefined if an error occurs
   *
   * @throws Error with the API error message (potentially transformed by mapError function)
   *
   * @example
   * ```typescript
   * // Basic usage with ID
   * const result = await patchData({ name: 'John' }, 123);
   * // Request URL: /api/users/123
   *
   * // Using optional path instead of ID
   * const result = await patchData({ status: 'active' }, undefined, 'profile');
   * // Request URL: /api/users/profile
   *
   * // No ID or path - uses base URL
   * const result = await patchData({ settings: { theme: 'dark' } });
   * // Request URL: /api/users
   * ```
   */
  async function patchData<T>(
    payload: T,
    id?: number | string,
    optionalPath?: number | string
  ): Promise<Response | undefined> {
    const requestPath = optionalPath
      ? `${url}/${optionalPath}`
      : id
      ? `${url}/${id}`
      : `${url}`;

    try {
      const response = await apiInstance(token).patch(
        requestPath,
        JSON.stringify({ id, ...payload })
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        let errorMessage = error.response?.data.message;
        if (typeof mapError === 'function') {
          errorMessage = mapError(error.response?.data.error, errorMessage);
        }
        if (captureException === 'admin') {
          throw captureAdminDataAccessError({
            error,
            message: errorMessage,
            requestPath,
            httpMethod: 'PATCH',
            queryKey: 'PatchDataWrapper error',
            operation: 'patch_data',
            extras: { payload },
            level: 'error',
          });
        }
        if (captureException === 'buyer') {
          buyerCaptureException(error.response?.data, (scope) => {
            scope.setTags({
              queryKey: 'PatchDataWrapper error',
              status_code:
                error instanceof AxiosError
                  ? error.response?.data.statusCode
                  : undefined,
            });
            scope.setContext('error', {
              error:
                error instanceof AxiosError ? error.response?.data : undefined,
            });
            scope.setExtra(url, payload);
            scope.setLevel('error');
          });
        }
        throw new Error(errorMessage);
      }
      return;
    }
  }

  return { patchData };
}
